import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAuthStore from '../../store/authStore';

const MODULE_ICONS = { OPD: '🏥', IPD: '🛏️', AE: '🚨', Lab: '🔬', Radiology: '📷', Pharmacy: '💊' };
const MODULE_COLORS = { OPD: '#3b82f6', IPD: '#7c3aed', AE: '#ef4444', Lab: '#0891b2', Radiology: '#059669', Pharmacy: '#d97706' };

function Badge({ label, color, bg }) {
  return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: bg, color }}>{label}</span>;
}

function TimelineEvent({ event, expanded, onToggle }) {
  const icon = MODULE_ICONS[event.module] || '📋';
  const color = MODULE_COLORS[event.module] || '#6b7280';
  const date = new Date(event.date);

  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: color + '20', border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ width: 2, flex: 1, backgroundColor: '#e5e7eb', marginTop: 4 }} />
      </div>
      <div style={{ flex: 1, paddingBottom: 20 }}>
        <div style={{ backgroundColor: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div onClick={onToggle} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Badge label={event.module} color={color} bg={color + '15'} />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{event.title}</span>
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>
                {date.toLocaleDateString('en-PK', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                {event.doctor && ` · Dr. ${event.doctor}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {event.status && <Badge label={event.status} color="#374151" bg="#f3f4f6" />}
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{expanded ? '▲' : '▼'}</span>
            </div>
          </div>
          {expanded && (
            <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f3f4f6' }}>
              {event.details && Object.entries(event.details).map(([key, val]) => val ? (
                <div key={key} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid #f9fafb' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', minWidth: 140, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{Array.isArray(val) ? val.join(', ') : val}</span>
                </div>
              ) : null)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const MODULES = ['OPD', 'IPD', 'AE', 'Lab', 'Radiology', 'Pharmacy'];

export default function PatientHistory360() {
  const { patient_id } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore(s => s.token);
  const headers = { Authorization: `Bearer ${token}` };

  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [activeModules, setActiveModules] = useState(new Set(MODULES));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [ptRes, tlRes] = await Promise.all([
          axios.get(`/api/patients/${patient_id}`, { headers }),
          axios.get(`/api/opd/patient/${patient_id}/timeline`, { headers, params: { from: dateFrom, to: dateTo } }),
        ]);
        setPatient(ptRes.data);
        setTimeline(tlRes.data?.events || []);
      } catch { setTimeline([]); }
      setLoading(false);
    };
    load();
  }, [patient_id, dateFrom, dateTo]);

  const toggleExpand = (id) => setExpandedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleModule = (mod) => setActiveModules(prev => {
    const next = new Set(prev);
    next.has(mod) ? next.delete(mod) : next.add(mod);
    return next;
  });

  const filtered = timeline.filter(e => activeModules.has(e.module));

  const printHistory = () => {
    const content = `<html><head><title>Patient History — ${patient?.name}</title>
    <style>body{font-family:Arial;padding:20px;font-size:12px}h2{margin:0 0 6px}table{width:100%;border-collapse:collapse}td,th{padding:4px 8px;border:1px solid #ddd}th{background:#f5f5f5}.event{margin-bottom:12px;padding:8px;border:1px solid #e5e7eb;border-radius:4px}</style></head><body>
    <h2>Patient Clinical History (360 View)</h2>
    <p><strong>${patient?.name}</strong> | MRN: ${patient?.mrn} | ${patient?.gender} | Age: ${patient?.age}y</p><hr/>
    ${filtered.map(e => `<div class="event"><strong>[${e.module}] ${e.title}</strong> — ${new Date(e.date).toLocaleDateString('en-PK')}${e.doctor ? ` | Dr. ${e.doctor}` : ''}</div>`).join('')}
    </body></html>`;
    const w = window.open('', '_blank', 'width=700,height=800');
    if (w) { w.document.open(); w.document.write(content); w.document.close(); w.print(); }
  };

  return (
    <div style={{ padding: 24, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, marginBottom: 8, padding: 0 }}>← Back</button>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Patient History (360°)</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>Complete clinical timeline</p>
        </div>
        <button onClick={printHistory} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Print History</button>
      </div>

      {/* Patient Card */}
      {patient && (
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{patient.name}</div>
              <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
                MRN: <strong>{patient.mrn}</strong> &nbsp;|&nbsp; {patient.gender} &nbsp;|&nbsp; Age: {patient.age}y &nbsp;|&nbsp; {patient.phone}
                {patient.blood_group && <> &nbsp;|&nbsp; <strong>{patient.blood_group}</strong></>}
              </div>
            </div>
            {patient.allergies && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>Allergies:</span>
                {patient.allergies.split(/[,;]/).map((a, i) => a.trim() && (
                  <span key={i} style={{ padding: '2px 8px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10, fontSize: 12 }}>⚠ {a.trim()}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #e5e7eb', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {MODULES.map(mod => (
            <button key={mod} onClick={() => toggleModule(mod)} style={{
              padding: '5px 12px', border: `1px solid ${activeModules.has(mod) ? MODULE_COLORS[mod] : '#e5e7eb'}`,
              borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: activeModules.has(mod) ? 600 : 400,
              backgroundColor: activeModules.has(mod) ? MODULE_COLORS[mod] + '15' : '#fff',
              color: activeModules.has(mod) ? MODULE_COLORS[mod] : '#9ca3af',
            }}>
              {MODULE_ICONS[mod]} {mod}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          <span style={{ alignSelf: 'center', color: '#9ca3af' }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        </div>
        <span style={{ fontSize: 13, color: '#9ca3af', marginLeft: 'auto' }}>{filtered.length} events</span>
      </div>

      {/* Timeline */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading history...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 16 }}>No history found</div>
        </div>
      ) : (
        <div>
          {filtered.map(event => (
            <TimelineEvent
              key={`${event.module}-${event.id}`}
              event={event}
              expanded={expandedIds.has(`${event.module}-${event.id}`)}
              onToggle={() => toggleExpand(`${event.module}-${event.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
